import type { Element } from "hast";
import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { type JournalFilter, stringifyFilters, useJournalFilterContext } from "@/contexts/JournalFilterContext";
import useNavigateTo from "@/hooks/useNavigateTo";
import { cn } from "@/lib/utils";
import { Routes } from "@/router";
import { JournalViewContext } from "../JournalView/JournalViewContext";

interface TickerProps extends React.HTMLAttributes<HTMLSpanElement> {
  node?: Element; // AST node from react-markdown
  "data-ticker"?: string;
  children?: React.ReactNode;
  showDollarSign?: boolean; // 是否显示 $ 符号（默认 true，用于 markdown 兼容）
  variant?: "default" | "lexical"; // 样式变体：default 用于 markdown，lexical 用于 Lexical 节点
  parentPage?: string; // 可选的父页面路径，用于替代从 Context 获取的值（在 TickerDetail 等场景中使用）
  clickable?: boolean; // 是否可以点击（默认 true，用于禁用点击的场景）
}

export const Ticker: React.FC<TickerProps> = ({
  "data-ticker": dataTicker,
  children,
  className,
  showDollarSign = true,
  variant = "default",
  parentPage: propParentPage,
  clickable = true,
  ...props
}) => {
  // 尝试从 Context 获取，如果 Context 不存在或未提供，则使用 prop 或默认值
  const context = useContext(JournalViewContext);
  const parentPage = propParentPage ?? context?.parentPage ?? Routes.ROOT;
  const location = useLocation();
  const navigateTo = useNavigateTo();
  const { getFiltersByFactor, removeFilter, addFilter } = useJournalFilterContext();

  const ticker = dataTicker || "";
  const normalizedTicker = ticker.toUpperCase(); // Normalize to uppercase

  const handleTickerClick = (e: React.MouseEvent) => {
    if (!clickable) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.stopPropagation();

    // If the ticker is clicked in a memo detail page, navigate to the memo list page
    if (location.pathname.startsWith("/m")) {
      const pathname = parentPage || Routes.ROOT;
      const searchParams = new URLSearchParams();

      searchParams.set("filter", stringifyFilters([{ factor: "tickerSearch", value: normalizedTicker }]));
      navigateTo(`${pathname}?${searchParams.toString()}`);
      return;
    }

    // Otherwise, use the filter context
    const isActive = getFiltersByFactor("tickerSearch").some((filter: JournalFilter) => filter.value === normalizedTicker);
    if (isActive) {
      removeFilter((f: JournalFilter) => f.factor === "tickerSearch" && f.value === normalizedTicker);
    } else {
      // Remove all existing ticker filters first, then add the new one
      removeFilter((f: JournalFilter) => f.factor === "tickerSearch");
      addFilter({
        factor: "tickerSearch",
        value: normalizedTicker,
      });
    }
  };

  // variant="lexical" 时不添加默认样式，完全依赖传入的 className
  const defaultClassName =
    variant === "lexical" ? "" : "inline-block w-auto text-primary cursor-pointer hover:opacity-80 transition-colors";

  return (
    <span className={cn(defaultClassName, className)} data-ticker={normalizedTicker} onClick={handleTickerClick} {...props}>
      {showDollarSign && <span className="text-muted-foreground mr-0.5">$</span>}
      {children || normalizedTicker}
    </span>
  );
};
